import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { registerCharts, barOptions, barDataset, CHART } from '../components/ChartTheme';
import api from '../axios';
import ModuleHeader from '../components/ModuleHeader';

registerCharts();

const TEAL = '#00F1A7';
const LIME = '#B4F300';

function StatCard({ icon, label, value, color = TEAL }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 5 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ bgcolor: color, color: '#0B1200', p: 1.5, borderRadius: 1, display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1, color }}>{value ?? '—'}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function ActionCard({ icon, title, description, buttonLabel, onClick, color = 'primary.main' }) {
  return (
    <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 5 }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{ bgcolor: color, color: '#0B1200', p: 1, borderRadius: 1, display: 'flex' }}>{icon}</Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button variant="contained" fullWidth onClick={onClick} aria-label={buttonLabel}
          sx={{ bgcolor: color, '&:hover': { filter: 'brightness(0.9)' } }}>
          {buttonLabel}
        </Button>
      </CardActions>
    </Card>
  );
}

export default function SecretariaDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gcsPendentes, setGcsPendentes] = useState([]);
  const [aprovando, setAprovando] = useState(null);
  const [cursosPendentes, setCursosPendentes] = useState([]);

  const carregarGCsPendentes = async () => {
    try {
      const res = await api.get('/grupos-crescimento/', { params: { aprovado: false } });
      setGcsPendentes(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const aprovarGC = async (gcId) => {
    setAprovando(gcId);
    try {
      await api.post(`/grupos-crescimento/${gcId}/aprovar`);
      setGcsPendentes(prev => prev.filter(g => g.id !== gcId));
    } catch (err) {
      setError(errorMsg(err) || 'Erro ao aprovar GC');
    }
    setAprovando(null);
  };

  const carregarCursosPendentes = async () => {
    try {
      const res = await api.get('/cursos/', { params: { status: 'rascunho' } });
      setCursosPendentes(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [membrosRes, formandosRes] = await Promise.all([
          api.get('/secretaria/membros').catch(() => ({ data: [] })),
          api.get('/formandos').catch(() => ({ data: [] }))
        ]);
        const membros = Array.isArray(membrosRes.data) ? membrosRes.data : [];
        const formandos = Array.isArray(formandosRes.data) ? formandosRes.data : [];
        setStats({
          membros: membros.length,
          formandos: formandos.length,
          aguardandoMigracao: formandos.filter(f => !f.migrado).length
        });
      } catch (e) {
        setError('Não foi possível carregar os dados.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    carregarGCsPendentes();
    carregarCursosPendentes();
  }, []);

  const chartLabels = ['Membros', 'Formandos', 'Aguardando migração'];
  const chartValues = [stats?.membros || 0, stats?.formandos || 0, stats?.aguardandoMigracao || 0];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Secretaria" title="Secretaria" />

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Indicadores */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <StatCard icon={<PeopleIcon />} label="Membros cadastrados" value={stats?.membros} color={TEAL} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard icon={<SchoolIcon />} label="Formandos (conclusão Start)" value={stats?.formandos} color={LIME} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard icon={<SwapHorizIcon />} label="Aguardando migração" value={stats?.aguardandoMigracao} color="warning.main" />
          </Grid>
        </Grid>
      )}

      {/* Visão geral (gráfico) */}
      <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 5, border: '1px solid', borderColor: 'divider', p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUpIcon sx={{ color: CHART.teal }} />
          <Typography variant="h3" sx={{ fontSize: '1rem' }}>Visão geral da Secretaria</Typography>
        </Box>
        <Box sx={{ height: 240 }}>
          <Bar data={{ labels: chartLabels, datasets: [{ ...barDataset(chartValues), label: 'Registros' }] }} options={barOptions()} />
        </Box>
      </Card>

      {/* Ações principais */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Ações</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<PeopleIcon />}
            title="Membros"
            description="Visualize e gerencie os membros oficiais da igreja cadastrados na base da Secretaria."
            buttonLabel="Ver membros"
            onClick={() => navigate('/membros-secretaria')}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<SchoolIcon />}
            title="Formandos"
            description="Alunos que concluíram as 8 aulas do Start e aguardam ser recebidos como membros."
            buttonLabel="Ver formandos"
            onClick={() => navigate('/formandos')}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<SwapHorizIcon />}
            title="Migração"
            description="Mova alunos aprovados no Start para a base oficial de membros da Secretaria."
            buttonLabel="Migrar alunos"
            onClick={() => navigate('/migracao-alunos')}
            color="primary.dark"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<UploadFileIcon />}
            title="Importar Membros"
            description="Importe membros a partir de uma planilha Excel (.xlsx) com nome, telefone, data de nascimento e estado civil."
            buttonLabel="Importar planilha"
            onClick={() => navigate('/importador-membros')}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<GroupIcon />}
            title="GCs Pendentes"
            description={`${gcsPendentes.length} GC(s) aguardando aprovação do pastor.`}
            buttonLabel={gcsPendentes.length > 0 ? `Aprovar (${gcsPendentes.length})` : 'Ver GCs'}
            onClick={() => gcsPendentes.length > 0 ? document.getElementById('gcs-pendentes-section')?.scrollIntoView({ behavior: 'smooth' }) : navigate('/grupos-crescimento')}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<SchoolIcon />}
            title="Cursos Pendentes"
            description={`${cursosPendentes.length} curso(s) aguardando aprovação.`}
            buttonLabel={cursosPendentes.length > 0 ? `Aprovar (${cursosPendentes.length})` : 'Ver cursos'}
            onClick={() => cursosPendentes.length > 0 ? document.getElementById('cursos-pendentes-section')?.scrollIntoView({ behavior: 'smooth' }) : navigate('/cursos')}
            color="secondary.dark"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ActionCard
            icon={<PeopleIcon />}
            title="Obreiros"
            description="Gestão dos obreiros da igreja: cargos, ordenações e histórico."
            buttonLabel="Abrir obreiros"
            onClick={() => navigate('/obreiros')}
            color="info.main"
          />
        </Grid>
      </Grid>

      {/* Cursos Pendentes */}
      {cursosPendentes.length > 0 && (
        <Box id="cursos-pendentes-section" sx={{ mt: 4, p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            <SchoolIcon sx={{ color: 'primary.main', mr: 1 }} />
            Cursos Pendentes de Aprovação ({cursosPendentes.length})
          </Typography>
          {cursosPendentes.map(curso => (
            <Box key={curso.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, mb: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{curso.titulo}</Typography>
                <Typography variant="caption" color="text.secondary">{curso.descricao?.substring(0, 80)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => navigate(`/cursos/${curso.id}`)}>Ver</Button>
                <Button size="small" variant="contained" color="success" onClick={async () => {
                  try { await api.put(`/cursos/${curso.id}`, { status: 'aberto' }); carregarCursosPendentes(); }
                  catch (e) { alert(e.response?.data?.detail || 'Erro'); }
                }}>Aprovar</Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* GCs Pendentes */}
      {gcsPendentes.length > 0 && (
        <Box id="gcs-pendentes-section" sx={{ mt: 4, p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            GCs Pendentes de Aprovação ({gcsPendentes.length})
          </Typography>
          {gcsPendentes.map(gc => (
            <Box key={gc.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, mb: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{gc.nome}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Líder: {gc.lider_nome || 'N/A'} | {gc.dia_semana || '?'} {gc.horario || ''} | {gc.bairro || ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => navigate(`/grupos-crescimento/${gc.id}`)}>
                  Ver
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  disabled={aprovando === gc.id}
                  onClick={() => aprovarGC(gc.id)}
                >
                  {aprovando === gc.id ? 'Aprovando...' : 'Aprovar'}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Fluxo explicativo */}
      <Box sx={{ mt: 5, p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Fluxo de entrada de membros</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label="1. Consolidação" sx={{ bgcolor: 'primary.dark', color: '#0B1200', fontWeight: 700 }} />
          <Typography variant="body2" color="text.secondary">→</Typography>
          <Chip label="2. Start (8 aulas)" sx={{ bgcolor: 'secondary.main', color: '#0B1200', fontWeight: 700 }} />
          <Typography variant="body2" color="text.secondary">→</Typography>
          <Chip label="3. Secretaria (membro)" sx={{ bgcolor: 'primary.main', color: '#0B1200', fontWeight: 700 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Visitantes são recebidos pela Consolidação, treinados no Start e então recebidos oficialmente como membros pela Secretaria.
        </Typography>
      </Box>
    </Container>
  );
}
